import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { PRODUCT_TYPE_MOVIE_REWARDS } from "@config/config";
import toSeoUrl from "@functions/toSeoUrl";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { SearchContext } from "@context/SearchContext";
import { UserContext } from "@context/UserContext";
import LinkTabs from "@components/LinkTabs";
import Loader from "@components/Ui/Loader";
import DateSelector from "@components/V3/DateSelector";
import ExperienceFinder from "@components/V3/ExperienceSelector/ExperienceFinder";
import MovieInformation from "@components/V3/MovieDetails/MovieInformation";
import MovieSessionList from "@components/V3/MovieDetails/MovieSessionList";
import VenueFinder from "@components/V3/VenueFinder";
import { requestUserLocation } from "@utils/locationUtils";

import "./style.scss";

const MovieSessionSelector = () => {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const { user, userCoords, setUserCoords } = useContext(AuthContext);
    const { productTypeId } = useContext(ApplicationContext);

    const {
        movie,
        isLoadingSessions,
        setVenueName,
        venueName,
        setSelectedExperiences,
        fetchMovie,
        fetchSessionsByMovie,
        primarySessions,
        secondarySessions,
        availableDates,
        venueHomeSearch,
        setVenueHomeSearch
    } = useContext(SearchContext);

    const { fetchApplyPromoAvailability } = useContext(UserContext);

    const [selection, setSelection] = useState({ date: null, venue: null, experience: null, session: null });
    const [selectedSessions, setSelectedSessions] = useState({ primary: [], secondary: [] });

    const hasFiredInitialDateClick = useRef(false);
    const [showInitialLoader, setShowInitialLoader] = useState(true);
    const ismovieRewardsSite = productTypeId === PRODUCT_TYPE_MOVIE_REWARDS;
    const [resolvedMovieTitle, setResolvedMovieTitle] = useState(null);

    // Resets state values when navigating to a movie page for the first time
    useEffect(() => {
        hasFiredInitialDateClick.current = false;
        setVenueName("");
        setVenueHomeSearch("");
        setSelectedExperiences([]);
        setSelection({ date: null, venue: null, experience: null, session: null });
    }, [id, location.pathname]);

    // Sets the movie state, obtains user coords, obtains previously selected venue, and calls initial sessions search on move page load
    useEffect(() => {
        const slugFromPath = location.pathname.split("/").pop();
        let isMounted = true;

        (async () => {
            const movieData = await fetchMovie(id, slugFromPath);
            if (!movieData?.title) return;

            const resolvedSlug = toSeoUrl(movieData.title);
            if (resolvedSlug !== slugFromPath) return;

            if (!isMounted) return;
            setResolvedMovieTitle(movieData.title);

            const venueNameFromUrl = new URLSearchParams(location.search).get("venue_name");
            if (!venueNameFromUrl && venueName) {
                history.replace({ pathname: location.pathname, search: `?venue_name=${toSeoUrl(venueName)}` });
            }
            
            const decodedVenue = venueNameFromUrl
                ? decodeURIComponent(venueNameFromUrl.replace(/-/g, " ")).replace(/\b\w/g, (c) => c.toUpperCase())
                : decodeURIComponent(venueHomeSearch.replace(/-/g, " ")).replace(/\b\w/g, (c) => c.toUpperCase());

            if (decodedVenue && !venueName) {
                setVenueName(decodedVenue);
                setSelection((prev) => ({ ...prev, venue: decodedVenue }));
            }

            const runFetch = (coords = null) => {
                fetchSessionsByMovie({
                    movieId: movieData.id,
                    venueName: decodedVenue || "",
                    lat: coords?.latitude,
                    lng: coords?.longitude,
                    search_distance: 25,
                });
            };

            if (userCoords?.latitude && userCoords?.longitude) {
                runFetch(userCoords);
            } else {
                requestUserLocation((coords) => {
                    if (coords) setUserCoords(coords);
                    runFetch(coords);
                });
            }

            if (isMounted) {
                setShowInitialLoader(false);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [id, location.pathname]);

    // Sets the selected date??
    useEffect(() => {
        if (
            // I need to figure out the below commented out line
            resolvedMovieTitle === movie?.title
        ) {
            if (availableDates.length > 0) {
                const firstDate = availableDates[0];
                setSelection((prev) => ({ ...prev, date: firstDate }));
                handleDateClick(firstDate);
                hasFiredInitialDateClick.current = true;
            }
        }
    }, [primarySessions, secondarySessions, movie?.title, resolvedMovieTitle, venueName, availableDates]);

    // Determines the subscriber's promo availability
    useEffect(() => {
        if (user?.id) fetchApplyPromoAvailability();
    }, [user?.id]);

    const handleDateClick = (date) => {
        setSelection((prev) => ({
            ...prev,
            date: prev.date === date ? `${date}` : date,
        }));

        setSelectedSessions((prev) => ({ 
            ...prev,
            primary: primarySessions[date] || {},
            secondary: secondarySessions[date] || {},
        }));
    };

    const handleVenueClick = (selectedVenue) => {
        if (!selectedVenue) {
            setSelection((prev) => ({ ...prev, venue: null }));
            setVenueName("");
            history.replace({ pathname: location.pathname });
            if (selection.date) handleDateClick(selection.date);
        } else {
            setSelection((prev) => ({ ...prev, venue: selectedVenue }));
            setVenueName(selectedVenue);
            history.replace({ pathname: location.pathname, search: `?venue_name=${toSeoUrl(selectedVenue)}` });
            if (selection.date) handleDateClick(selection.date);
        }
    };

    const handleExperienceClick = (updatedSelections) => {
        setSelectedExperiences(updatedSelections);

        if (selection.date) {
            handleDateClick(selection.date);
        }
    };

    return (
        <>
            <div className="sessions-available__inner">
                <LinkTabs>
                    {ismovieRewardsSite && (
                        <div label="Dates & Sessions">
                            <div className="sessions-available__wrapper">
                                {showInitialLoader || isLoadingSessions ? (
                                    <Loader isLoading={showInitialLoader || isLoadingSessions} text="Fetching Sessions" />
                                ) : (
                                    <>
                                        <DateSelector
                                            uniqueDates={availableDates}
                                            selectedDate={selection.date}
                                            handleClickDate={handleDateClick}
                                        />

                                        <VenueFinder
                                            selectedVenue={selection.venue}
                                            handleVenueClick={(venueName) => handleVenueClick(venueName)}
                                        />

                                        <ExperienceFinder
                                            selection={selection}
                                            handleExperienceClick={handleExperienceClick}
                                        />
                                        
                                        <MovieSessionList
                                            venueName={selection.venue}
                                            movieTitle={movie?.title}
                                            selection={selection}
                                            selectedSessions={selectedSessions}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <div label="Details">
                        <MovieInformation movie={movie} />
                    </div>
                </LinkTabs>
            </div>
        </>
    );
};

export default MovieSessionSelector;
